<template>
  <div class="tabs-widget" :style="widgetStyles">
    <div v-if="widget.title" class="tabs-title mb-3">
      <h3 :style="titleStyles">{{ widget.title }}</h3>
    </div>

    <div class="tabs-container">
      <!-- Tab Navigation -->
      <ul 
        class="nav nav-tabs tabs-nav" 
        :class="[tabStyleClass, tabAlignmentClass]"
        role="tablist"
      >
        <li 
          v-for="tab in widget.tabs" 
          :key="tab.id"
          class="nav-item"
          role="presentation"
        >
          <button 
            class="nav-link"
            :class="{ 'active': activeTabId === tab.id }"
            type="button"
            role="tab"
            :aria-controls="`tab-${widget.id}-${tab.id}`"
            :aria-selected="activeTabId === tab.id"
            @click="setActiveTab(tab.id)"
            :style="tabButtonStyles"
          >
            <i v-if="tab.icon" :class="tab.icon + ' me-2'"></i>
            {{ tab.title }}
          </button>
        </li>
      </ul>

      <!-- Tab Content -->
      <div class="tab-content tabs-content">
        <Transition name="tab-slide" mode="out-in">
          <div
            v-if="activeTab"
            :key="activeTab.id"
            class="tab-pane show active"
            :id="`tab-${widget.id}-${activeTab.id}`"
            role="tabpanel"
          >
            <div
              class="tab-pane-content"
              :style="tabContentStyles"
              v-html="processedContent(activeTab.content)"
            ></div>
          </div>
        </Transition>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted } from 'vue'
import type { TabsWidget } from '@/types/pagebuilder'
import { resolveWidgetBackgroundStyle } from '@/utils/backgroundGradient'
import { processRichTextContent } from '@/utils/textWidgetUtils'

interface Props {
  widget: TabsWidget
}

const props = defineProps<Props>()

// Active tab state
const activeTabId = ref<string>('')

// Initialize with first tab or specified active tab
onMounted(() => {
  if (props.widget.activeTab && props.widget.tabs.some(tab => tab.id === props.widget.activeTab)) {
    activeTabId.value = props.widget.activeTab
  } else if (props.widget.tabs.length > 0) {
    activeTabId.value = props.widget.tabs[0].id
  }
})

// Computed styles
const widgetStyles = computed(() => ({
  padding: props.widget.padding || '1rem 0',
  ...resolveWidgetBackgroundStyle(props.widget),
}))

const titleStyles = computed(() => ({
  color: props.widget.titleColor || '#333',
  fontSize: props.widget.titleSize || '1.5rem',
  textAlign: props.widget.titleAlignment || 'left',
  margin: '0 0 1rem 0'
}))

const tabStyleClass = computed(() => {
  const style = props.widget.tabStyle || 'default'
  return {
    'nav-pills': style === 'pills',
    'nav-underline': style === 'underline',
    'nav-tabs': style === 'default'
  }
})

const tabAlignmentClass = computed(() => {
  const alignment = props.widget.tabAlignment || 'left'
  return {
    'justify-content-start': alignment === 'left',
    'justify-content-center': alignment === 'center',
    'justify-content-end': alignment === 'right'
  }
})

const tabButtonStyles = computed(() => ({
  color: props.widget.tabColor || undefined,
}))

const tabContentColor = computed(() => props.widget.textColor || '#495057')

const tabContentStyles = computed(() => ({
  color: tabContentColor.value,
  '--widget-text-color': tabContentColor.value,
}))

const activeTab = computed(() =>
  props.widget.tabs.find((tab) => tab.id === activeTabId.value) ?? null,
)

// Methods
const setActiveTab = (tabId: string) => {
  activeTabId.value = tabId
}

const processedContent = (content: string): string => {
  return processRichTextContent(content, tabContentColor.value)
}
</script>

<style scoped>
.tabs-widget {
  max-width: 100%;
}

.tabs-title h3 {
  margin: 0;
  font-weight: 600;
}

.tabs-container {
  background: transparent;
  border-radius: 0.375rem;
  overflow: hidden;
}

/* Tab Navigation Styles */
.tabs-nav {
  border-bottom: 1px solid #dee2e6;
  margin-bottom: 0;
  background-color: #f8f9fa;
  padding: 0.5rem 1rem 0;
}

.nav-tabs .nav-link {
  color: #495057;
  background-color: transparent;
  border: 1px solid transparent;
  border-top-left-radius: 0.375rem;
  border-top-right-radius: 0.375rem;
  padding: 0.75rem 1.25rem;
  font-weight: 500;
  transition: all 0.15s ease-in-out;
  cursor: pointer;
}

.nav-tabs .nav-link:hover {
  border-color: #e9ecef #e9ecef #dee2e6;
  isolation: isolate;
  color: #0d6efd;
  transform: translateY(-2px);
}

.nav-tabs .nav-link.active {
  color: #0d6efd;
  background-color: #fff;
  border-color: #dee2e6 #dee2e6 #fff;
  transform: translateY(0);
}

/* Tab Slide Transition */
.tab-slide-enter-active,
.tab-slide-leave-active {
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
}

.tab-slide-enter-from {
  opacity: 0;
  transform: translateX(20px);
}

.tab-slide-leave-to {
  opacity: 0;
  transform: translateX(-20px);
}

.tab-slide-enter-to,
.tab-slide-leave-from {
  opacity: 1;
  transform: translateX(0);
}

/* Pills Style */
.nav-pills .nav-link {
  color: #495057;
  background-color: transparent;
  border-radius: 0.375rem;
  padding: 0.75rem 1.25rem;
  margin-right: 0.5rem;
  font-weight: 500;
  transition: all 0.15s ease-in-out;
  cursor: pointer;
}

.nav-pills .nav-link:hover {
  background-color: #e9ecef;
  color: #0d6efd;
}

.nav-pills .nav-link.active {
  color: #fff;
  background-color: #0d6efd;
}

/* Underline Style */
.nav-underline {
  border-bottom: 2px solid #dee2e6;
}

.nav-underline .nav-link {
  color: #495057;
  background-color: transparent;
  border: none;
  border-bottom: 2px solid transparent;
  border-radius: 0;
  padding: 0.75rem 1.25rem;
  font-weight: 500;
  transition: all 0.15s ease-in-out;
  cursor: pointer;
}

.nav-underline .nav-link:hover {
  border-bottom-color: #0d6efd;
  color: #0d6efd;
}

.nav-underline .nav-link.active {
  color: #0d6efd;
  border-bottom-color: #0d6efd;
}

/* Tab Content */
.tabs-content {
  background-color: transparent;
  border-radius: 0 0 0.375rem 0.375rem;
}

.tab-pane {
  display: none;
}

.tab-pane.show.active {
  display: block;
}

.tab-pane-content {
  padding: 1.5rem;
  line-height: 1.6;
}

.tab-pane-content :deep(p),
.tab-pane-content :deep(h1),
.tab-pane-content :deep(h2),
.tab-pane-content :deep(h3),
.tab-pane-content :deep(h4),
.tab-pane-content :deep(h5),
.tab-pane-content :deep(h6),
.tab-pane-content :deep(li),
.tab-pane-content :deep(span),
.tab-pane-content :deep(div),
.tab-pane-content :deep(blockquote),
.tab-pane-content :deep(strong),
.tab-pane-content :deep(em) {
  color: var(--widget-text-color, inherit) !important;
}

/* Responsive Design */
@media (max-width: 768px) {
  .tabs-nav {
    padding: 0.25rem 0.5rem 0;
    flex-wrap: wrap;
  }
  
  .nav-tabs .nav-link,
  .nav-pills .nav-link,
  .nav-underline .nav-link {
    padding: 0.5rem 0.75rem;
    font-size: 0.9rem;
    margin-bottom: 0.25rem;
  }
  
  .nav-pills .nav-link {
    margin-right: 0.25rem;
  }
  
  .tab-pane-content {
    padding: 1rem;
    font-size: 0.9rem;
  }
  
  .tabs-nav.justify-content-center,
  .tabs-nav.justify-content-end {
    justify-content: flex-start !important;
  }
}

@media (max-width: 576px) {
  .nav-item {
    flex: 1;
    text-align: center;
  }
  
  .nav-link {
    font-size: 0.8rem !important;
    padding: 0.5rem 0.25rem !important;
  }
}

/* Animation */
.tab-pane {
  transition: opacity 0.3s ease-in-out;
}

.fade:not(.show) {
  opacity: 0;
}

.fade.show {
  opacity: 1;
}

/* Custom Colors */
.tabs-widget[data-color="primary"] .nav-tabs .nav-link.active,
.tabs-widget[data-color="primary"] .nav-pills .nav-link.active,
.tabs-widget[data-color="primary"] .nav-underline .nav-link.active {
  color: #0d6efd;
  border-color: #0d6efd;
  background-color: #0d6efd;
}

.tabs-widget[data-color="success"] .nav-tabs .nav-link.active,
.tabs-widget[data-color="success"] .nav-pills .nav-link.active,
.tabs-widget[data-color="success"] .nav-underline .nav-link.active {
  color: #198754;
  border-color: #198754;
  background-color: #198754;
}

.tabs-widget[data-color="danger"] .nav-tabs .nav-link.active,
.tabs-widget[data-color="danger"] .nav-pills .nav-link.active,
.tabs-widget[data-color="danger"] .nav-underline .nav-link.active {
  color: #dc3545;
  border-color: #dc3545;
  background-color: #dc3545;
}

.tabs-widget[data-color="warning"] .nav-tabs .nav-link.active,
.tabs-widget[data-color="warning"] .nav-pills .nav-link.active,
.tabs-widget[data-color="warning"] .nav-underline .nav-link.active {
  color: #fd7e14;
  border-color: #fd7e14;
  background-color: #fd7e14;
}
</style>